import { useMutation } from '@apollo/client/react';
import { Flex, ValidationText } from '@utilitywarehouse/hearth-react';
import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';

import { useBroadbandSubmitter } from '@features/Broadband/useBroadbandSubmitter';
import { useBundleBasket } from '@hooks/useBundleBasket';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { BroadbandFormValues } from '@src/types/broadband';
import { BooleanSelection } from '@src/types/index';
import { FindStatus, OrderType } from '@src/types/oneTouchSwitching';
import { YourDetailsFormValues } from '@src/types/yourDetails';
import { formatAddressLines } from '@utils/formatAddressLines';

import {
  addSwitch as addSwitchMutation,
  addSwitchingRequirements as addSwitchingRequirementsMutation,
} from '../query';
import { useOneTouchSwitchTrackEvents } from '../useOneTouchSwitchTrackEvents';
import { SwitchTile } from './SwitchTile/SwitchTile';

const OneTouchSwitching: React.FC = () => {
  const { values, errors, touched, setFieldValue } =
    useFormikContext<YourDetailsFormValues>();
  const { basketId } = useBasketContext();
  const { basket, basketRaw } = useBundleBasket({
    skipTrackingContextUpdate: true,
  });
  const address = basketRaw?.details.supplyAddress;
  const [firstAttemptFailed, setFirstAttemptFailed] = useState(false);
  const { canUpdateState } = useAppointmentContext();

  const [executeAddSwitch] = useMutation(addSwitchMutation);
  const [addSwitchingRequirements] = useMutation(
    addSwitchingRequirementsMutation
  );
  const { handleSubmit } = useBroadbandSubmitter(basket);

  useEffect(() => {
    if (values.otsFindStatus === FindStatus.NOT_FOUND) {
      setFirstAttemptFailed(true);
    }
  }, [values.otsFindStatus]);

  useOneTouchSwitchTrackEvents(values, firstAttemptFailed);

  const selectedQuote = basket?.broadband.quotes.find((q) => q.selected);
  const isBroadbandOnly = selectedQuote?.product.details.broadbandOnly ?? true;

  const phoneRequirement = selectedQuote?.requirements.find(
    (r) => r.requirement.lineCharacteristics?.cli
  );
  const phoneNumber = phoneRequirement?.requirement.lineCharacteristics?.cli;
  const broadbandValues = basket?.broadband.yourService;

  const addSwitch = () => {
    executeAddSwitch({
      variables: {
        input: {
          basketId,
          isSwitch: values.otsHasExistingLine,
          providerNotListed: values.otsProviderNotListed,
          losingProvider: values.otsSelectedProvider?.id,
          quoteId: selectedQuote?.id ?? '',
        },
      },
    });

    addSwitchingRequirements({
      variables: {
        details: {
          basketId,
          switching: {
            explicitConsent: values.otsHasExistingLine,
            correlationId: values.otsMatchId,
            losingProvider: values.otsSelectedProvider?.id,
            losingProviderAccountNumber: values.otsAccountReference,
            switchOrderReference: values.otsSwitchingReference,
          },
        },
      },
    });
  };

  useEffect(() => {
    if (
      (!values.otsHasExistingLine ||
        values.otsProviderNotListed ||
        [FindStatus.FAIL, FindStatus.SUCCESS].includes(values.otsFindStatus)) &&
      canUpdateState
    ) {
      addSwitch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.otsFindStatus,
    values.otsHasExistingLine,
    values.otsProviderNotListed,
  ]);

  useEffect(() => {
    if (
      values.otsHomePhoneReference &&
      values.otsFindStatus === FindStatus.SUCCESS
    ) {
      let fields: BroadbandFormValues = {
        wifiUpgrade: broadbandValues?.wifiUpgrade ?? false,
        landline: broadbandValues?.landline ?? '',
        callPackage: broadbandValues?.callPackage ?? BooleanSelection.No,
        callPackageId: broadbandValues?.callPackageId ?? null,
        selectedTariff: broadbandValues?.selectedTariff ?? '',
        includeVoip: broadbandValues?.includeVoip ?? null,
        isOTS: true,
      };
      fields = { ...fields, landline: values.otsHomePhoneReference.toString() };
      handleSubmit(fields);
    }
  }, [
    handleSubmit,
    values.otsFindStatus,
    values.otsHomePhoneReference,
    broadbandValues?.callPackage,
    broadbandValues?.callPackageId,
    broadbandValues?.landline,
    broadbandValues?.selectedTariff,
    broadbandValues?.wifiUpgrade,
    broadbandValues?.includeVoip,
  ]);

  if (!basket || !selectedQuote || !address) return null;

  return (
    <>
      <SwitchTile
        orderType={
          isBroadbandOnly
            ? OrderType.BROADBAND_ONLY
            : OrderType.BROADBAND_AND_VOICE
        }
        currentAddress={{
          addressLines: formatAddressLines(address),
          postCode: address?.postcode,
          postTown: address?.postTown ?? '',
          uprn: address?.uprn ?? '',
        }}
        numberToPort={phoneNumber ?? null}
        setFieldValue={setFieldValue}
      />
      {errors.otsFindStatus && touched.otsFindStatus && (
        <Flex className="details__ots-error" id="otsFindStatus">
          <ValidationText status="invalid">
            {errors.otsFindStatus}
          </ValidationText>
        </Flex>
      )}
    </>
  );
};

export { OneTouchSwitching };
